/* eslint-disable linebreak-style */
import React from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import {
  Dialog,
  DialogFooter,
  PrimaryButton,
} from '@fluentui/react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useLogger } from '../Logger';
import { NotificationType, useNotificationApi } from '../NotificationApi';

type Tokens = {
  accessToken?: string,
  idToken?: string,
};

type TokenContextValue = Tokens & {
  refreshInteractively: Function,
  logout: Function,
};

// TODO: Split into two contexts. Tokens shouldn't be exposed
const TokenContext = React.createContext<TokenContextValue>({
  accessToken: undefined,
  idToken: undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  refreshInteractively: (login: boolean = false) => { },
  logout: () => { },
});

type TokenProviderProps = React.PropsWithChildren<{
  scopes: string[],
}>;

export const useToken = (): TokenContextValue => React.useContext(TokenContext);

export const TokenProvider: React.FunctionComponent<TokenProviderProps> = ({
  scopes,
  children,
}) => {
  const { instance, accounts } = useMsal();
  const logger = useLogger();
  const account = useAccount(accounts[0] || {});
  const [tokens, setTokens] = React.useState<Tokens>();
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const { showNotification } = useNotificationApi();

  React.useEffect(() => {
    if (account?.homeAccountId) {
      logger.setAppendedData('HomeAccountId', account.homeAccountId);
    }
  }, [account?.homeAccountId, logger]);

  const refreshInteractively = React.useCallback((login: boolean) => {
    setTokens({});

    if (login) {
      instance.loginRedirect({
        scopes,
        // As we are moving to /common endpoint, we might have a hard time switching between AAD and MSA without this
        // Yet better to remove it in Production
        prompt: 'select_account',
      });
    } else {
      // Try to refresh with refresh token first
      // eslint-disable-next-line no-lonely-if
      if (account) {
        instance.acquireTokenSilent({
          scopes,
          account,
          // https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4206
          forceRefresh: true,
        }).then((response) => {
          setTokens({
            accessToken: response.accessToken,
            idToken: response.idToken,
          });
        }, (err) => {
          if (err instanceof InteractionRequiredAuthError) {
            logger.logInfo('User interaction required to refresh tokens');
            setIsDialogOpen(true);
          } else {
            logger.logError('Failed to refresh tokens silently');
            showNotification('Internal error with authentication. Please contact developers.', NotificationType.error);
          }
        });
      } else {
        setIsDialogOpen(true);
      }
    }
  }, [instance, logger, account, scopes, setIsDialogOpen, showNotification]);

  React.useEffect(() => {
    const handle = setInterval(() => {
      const currentEpoch: number = Math.floor(Date.now() / 1000);
      // Exp claim comes as a UNIX Epoch
      const secondsToExpire = (account?.idTokenClaims?.exp ?? 0) - currentEpoch;

      // 15 Minutes
      if (secondsToExpire < 60 * 15) {
        refreshInteractively(false);
      }
    }, 1000 * 15);

    return () => clearInterval(handle);
  }, [account?.idTokenClaims?.exp, refreshInteractively]);

  React.useEffect(() => {
    if (account) {
      instance.acquireTokenSilent({
        scopes,
        account,
        forceRefresh: true,
      }).then((response) => {
        if (response) {
          setTokens({
            accessToken: response.accessToken,
            idToken: response.idToken,
          });
        }
      }, (err) => {
        if (err instanceof InteractionRequiredAuthError) {
          logger.logInfo('User interaction required to refresh tokens');
          setIsDialogOpen(true);
        } else {
          logger.logError('Failed to refresh tokens silently');
          showNotification('Internal error with authentication. Please contact developers.', NotificationType.error);
        }
      });
    }
  }, [account, instance, scopes, setIsDialogOpen, logger, showNotification]);

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <TokenContext.Provider value={{
      ...tokens,
      refreshInteractively,
      logout: () => instance.logoutRedirect({
        account,
        logoutHint: account?.username,
      }),
    }}
    >
      {children}
      <Dialog
        hidden={!isDialogOpen}
        dialogContentProps={{
          title: 'Session Expired',
          subText: 'Your sign-in has expired. Please click the button below to sign in again.',
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={() => instance.loginRedirect({ scopes })} text="Login" />
        </DialogFooter>
      </Dialog>
    </TokenContext.Provider>
  );
};
